// ─── Training Data ────────────────────────────────────────────────────────────
// To add a new training module: append an object to the `modules` array.
// To add product cards to a module: append to that module's `cards` array.
//
//  status: "completed" | "in_progress" | "locked"
//  cards:  array of { title, body } shown as collapsible product-training cards
//  required: true  → shows a "Required" badge

export const TRAINING_DATA = {
  modules: [
    {
      id: "tm_company",
      title: "Propertunity 101",
      subtitle: "Company overview & culture",
      duration: "15 min",
      status: "completed",
      color: "#2563eb",
      lessons: [
        "Who we are",
        "Lem Lux Realty partnership",
        "Your role in the ecosystem",
      ],
      cards: [
        {
          title: "Who We Are",
          body: "Propertunity Capital is a real-estate lending platform that connects investors with fast, flexible financing. We partner with Lem Lux Realty to provide seamless bridge and term loan solutions across Florida and beyond.",
        },
        {
          title: "Your Role in the Ecosystem",
          body: "As a Sales Representative you are the first point of contact for borrowers. Your job is to qualify, educate, and guide investors to the right product — then hand off to the underwriting team for execution.",
        },
        {
          title: "Culture & Values",
          body: "We move fast and communicate clearly. No deal is too small to treat with professionalism. Every rep is expected to follow up within 24 hours and keep the CRM updated at all times.",
        },
      ],
    },
    {
      id: "tm_bridge",
      title: "Bridge Loans Deep Dive",
      subtitle: "Short-term financing for flips & construction",
      duration: "25 min",
      status: "completed",
      color: "#0ea5e9",
      lessons: [
        "What is a bridge loan?",
        "Qualifying criteria",
        "Common objections & rebuttals",
        "Underwriting basics",
      ],
      cards: [
        {
          title: "What Is a Bridge Loan?",
          body: "A bridge loan is a short-term, asset-based loan typically 6–24 months. It 'bridges' the gap between a purchase or renovation and a long-term refinance or sale. Speed is the core value proposition — closings in as little as 5–7 business days.",
        },
        {
          title: "Qualifying Criteria",
          body: "Key factors: property value (LTV typically ≤ 75%), borrower experience, exit strategy, and property condition. Credit score matters less than for conventional loans — we primarily underwrite the deal, not the borrower.",
        },
        {
          title: "Common Objections & Rebuttals",
          body: '"The rate is too high" → Remind them the rate is short-term. A 10% bridge loan on a 6-month flip is far cheaper than missing the deal. "I can get a bank loan" → Ask them when. Speed and certainty of close are worth the premium.',
        },
        {
          title: "Underwriting Basics",
          body: "Underwriting looks at: ARV (after-repair value), purchase price + rehab budget, borrower track record, and exit strategy. Have these four items ready before you submit. Incomplete files are the #1 cause of delays.",
        },
      ],
    },
    {
      id: "tm_dscr",
      title: "Term Loans & Buy-to-Rent",
      subtitle: "Long-term DSCR & rental financing",
      duration: "20 min",
      status: "in_progress",
      color: "#7c3aed",
      lessons: [
        "DSCR explained",
        "Rental portfolio strategy",
        "Rate & term structure",
        "Qualifying investors",
      ],
      // ── DSCR cards ──────────────────────────────────────────────────────────
      cards: [
        {
          title: "DSCR Explained",
          body: "Debt Service Coverage Ratio = Net Operating Income ÷ Annual Debt Service. A DSCR of 1.0 means rent covers the mortgage exactly. We typically require ≥ 1.1. This metric replaces traditional income verification for investors.",
        },
        {
          title: "The DSCR Formula: Rent ÷ PITIA",
          body: "DSCR = Monthly Gross Rent ÷ PITIA. PITIA stands for: P — Principal (reduces loan balance), I — Interest (cost of borrowing), T — Taxes (annual ÷ 12), I — Insurance (hazard/homeowner ÷ 12), A — Association dues (HOA, condo fees, co-op maintenance). The property qualifies itself — no W-2s or tax returns needed.",
        },
        {
          title: "DSCR Thresholds Cheat Sheet",
          body: "DSCR < 0.75: Only No Ratio Term or Credit Flex. Very limited leverage.\nDSCR 0.75–0.99: DSCR 1-4 still works for 700+ credit at lower LTV/higher rate. Under 700 credit? Use No Ratio Term.\nDSCR 1.0–1.24: Standard DSCR 1-4 territory. Pricing improves as DSCR rises.\nDSCR 1.25+: The sweet spot. Best rates and most flexible terms.",
        },
        {
          title: "Real-World DSCR Examples",
          body: "Sweet Spot (1.25): Tampa condo, rent $2,500, PITIA $2,000 → 1.25. Maria is self-employed with no W-2 — approved, no tax returns needed.\n\nThe Squeaker (0.90): Phoenix condo, rent $1,800, PITIA $2,000 → 0.90. Jake has 700+ credit so DSCR 1-4 still works at lower LTV; under 700 he'd need No Ratio Term.\n\nCash Cow (1.60): Indy duplex, rent $3,200, PITIA ~$1,750 → 1.60. Priya gets best available rates — lender's dream.",
        },
        {
          title: "The HOA Trap — Always Ask",
          body: "Forgetting to include HOA/condo fees in PITIA is the #1 rookie mistake. Example: Miami Beach condo, rent $3,200, loan $300K at 7.75%. Without HOA ($450/mo): PITIA = $2,748, DSCR = 1.16 ✓. With HOA: PITIA = $3,198, DSCR = 1.00 — barely alive. That single $450 fee kills a healthy deal. Always ask about association dues, especially on condos.",
        },
        {
          title: "The Four DSCR Programs",
          body: "1. DSCR 1-4 Unit — Default. Up to 85% LTV, min DSCR 1.0+, 660+ credit. Best rates. Start here every time.\n\n2. DSCR Multi (5–9 Units) — Up to 75% LTV, 660+ credit. Requires full rent roll + T12 operating statement.\n\n3. No Ratio Term — No DSCR minimum, up to 75% LTV, 660+ credit. Higher rates. Use when the math doesn't work.\n\n4. Credit Flex — Sub-660 credit OK, no DSCR minimum, up to 65% LTV. Highest rates. Last resort.\n\nAlways funnel top-down: DSCR 1-4 → No Ratio → Credit Flex.",
        },
        {
          title: "Rate Adjustments — How They Stack",
          body: "Base rate is the starting point. Each risk factor adds or subtracts:\n• LTV: Higher LTV = higher rate (80% costs more than 70%)\n• Credit: 700 costs more than 760+\n• Interest-only: typically +0.25–0.50%\n• Prepay penalty: shorter prepay = higher rate; 5-year prepay can save ~0.50%\n• Property type: condos and 2-4 units cost more than SFRs\n\nExample: 7.00% base + 0.25% (LTV 75-80%) + 0.375% (credit 700-719) + 0.50% (IO) + 0.25% (condo) = 8.375% final rate. On a $300K loan that's ~$250/mo more than a clean SFR deal.",
        },
        {
          title: "Prepay Penalty Trade-Off",
          body: "A longer prepay penalty lowers the rate because the lender is guaranteed a longer income stream:\n• No prepay: Highest rate, maximum flexibility\n• 3-year prepay: Standard, balanced\n• 5-year prepay: Lowest rate, borrower locked in 5 years\n\nAlways show two scenarios — one with short prepay and one with long. A borrower who says 'I don't want a prepay' often changes their mind when they see the rate difference.",
        },
        {
          title: "Short-Term Rentals (Airbnb/VRBO)",
          body: "STR properties can generate more income than traditional rentals but lenders are cautious about income stability. Accepted documentation: (1) Trailing 12-month Airbnb/VRBO platform statements, (2) STR appraisal addendum (appraiser estimates STR market rent), (3) CPA-prepared P&L, (4) AirDNA market data as supporting evidence. Gold standard: 12+ months of actual platform statements. Under 6 months? Expect pushback — use STR appraisal addendum.",
        },
        {
          title: "Entity Borrowers: LLC & Trust",
          body: "Most investors hold rental property in an LLC or trust for liability protection. What changes:\n• LLC: Need articles of organization, operating agreement, EIN, certificate of good standing\n• Trust: Need the trust agreement (to verify trustee and powers)\n• Personal guarantee: Always required from an individual — even with an entity\n• Credit check: Runs on the individual guarantor, NOT the entity\n\nCommon misconception: 'My LLC has great credit.' LLCs don't have FICO scores. The guarantor's personal credit is all that matters.",
        },
        {
          title: "Multi-Unit (5–9) Documentation",
          body: "Crossing the 4-unit threshold means more commercial-style underwriting:\n• Rent roll: Every unit, tenant name, lease dates, and monthly rent\n• T12 operating statement: Trailing 12-month actual income and expenses (not projected)\n• Vacancy factor: Lenders may haircut income 5–10% for vacancy/collection loss\n• Expense ratio: Operating expenses as % of gross income\n\nHave these docs ready before you submit — incomplete files are the #1 cause of delays.",
        },
        {
          title: "Rental Portfolio Strategy",
          body: "Investors building portfolios want a lender who can scale with them. Emphasize our blanket loan options and portfolio-level pricing. A rep who thinks in portfolios — not single deals — becomes an indispensable advisor.",
        },
        {
          title: "Rate & Term Structure",
          body: "30-year amortization is standard. Rates are tied to the SOFR index plus a margin. Interest-only periods of 1–3 years are available for cash-flow optimization. Always present rate in the context of cash-on-cash return.",
        },
        {
          title: "Qualifying Investors",
          body: "Target: experienced landlords (2+ properties), strong DSCR properties, clean entity structure (LLC preferred). Red flags: negative cash flow, first-time landlord on a multi-unit, or no reserves.",
        },
        // ── DSCR Calculator card — rendered by the training UI using the
        //    `component` key. Set type: "calculator" to trigger DSCRCalculator.
        {
          title: "DSCR Calculator — Try It Yourself",
          body: "Use the interactive calculator below to run real deal numbers. Punch in rent, loan details, taxes, insurance, and HOA to instantly see DSCR and which program the deal qualifies for.",
          type: "calculator",       // ← signals the card renderer to embed the component
          component: "DSCRCalculator",
        },
      ],
    },
    {
      id: "tm_discovery",
      title: "The Discovery Call Playbook",
      subtitle: "Qualify, pitch, and close in 20 minutes",
      duration: "30 min",
      status: "locked",
      color: "#16a34a",
      lessons: [
        "Opening frameworks",
        "Needs assessment questions",
        "Presenting loan options",
        "Handling stalls",
      ],
      cards: [
        {
          title: "Opening Frameworks",
          body: "Start with a warm, confident opener: state your name, company, and one clear value statement. Then ask a broad qualifying question to get the borrower talking. Never pitch products before you understand their situation.",
        },
        {
          title: "Needs Assessment Questions",
          body: "The four questions that drive every discovery call: (1) What's the deal? (2) What's your timeline? (3) Have you financed with us before? (4) What matters most — speed, rate, or leverage? Listen more than you talk.",
        },
        {
          title: "Presenting Loan Options",
          body: "Present two options, not one. Give a recommended option and an alternative. This gives the borrower the feeling of choice while anchoring them to your recommendation. Always connect the product back to their stated need.",
        },
        {
          title: "Handling Stalls",
          body: '"I need to think about it" = missing information or missing trust. Ask: "What would you need to see to move forward today?" Silence after a stall is your best tool — resist the urge to fill it.',
        },
      ],
    },
    {
      id: "tm_crm",
      title: "CRM Mastery",
      subtitle: "Work your pipeline like a pro",
      duration: "20 min",
      status: "locked",
      color: "#ea580c",
      lessons: [
        "Pipeline stages",
        "Activity logging",
        "Follow-up cadences",
        "Reporting & forecasting",
      ],
      cards: [
        {
          title: "Pipeline Stages",
          body: "Our CRM uses five stages: New Lead → Contacted → Qualified → Term Sheet Sent → Funded. A lead should never sit in one stage for more than 5 business days without an activity logged against it.",
        },
        {
          title: "Activity Logging",
          body: "Log every call, email, and text within 2 hours of the interaction. Include outcome and next step. Managers review activity logs weekly — a clean pipeline reflects professional habits.",
        },
        {
          title: "Follow-Up Cadences",
          body: "Day 1: call. Day 3: email. Day 7: call + text. Day 14: breakup email. After 30 days of no response, move to a monthly newsletter cadence. Persistence with professionalism closes more deals than a single perfect pitch.",
        },
        {
          title: "Reporting & Forecasting",
          body: "Review your pipeline every Monday morning. Identify deals that could close this month and plan specific actions for each. Your forecast is only as accurate as your data — garbage in, garbage out.",
        },
      ],
    },
    {
      id: "tm_compliance",
      title: "Compliance & Fair Lending",
      subtitle: "Required — legal & regulatory basics",
      duration: "35 min",
      status: "locked",
      color: "#dc2626",
      required: true,
      lessons: [
        "RESPA overview",
        "Fair lending laws",
        "Privacy & data handling",
        "Do's and don'ts",
      ],
      cards: [
        {
          title: "RESPA Overview",
          body: "The Real Estate Settlement Procedures Act governs how we disclose fees and handle referrals. Never accept or give anything of value in exchange for a referral. All affiliated business arrangements must be disclosed in writing.",
        },
        {
          title: "Fair Lending Laws",
          body: "The Equal Credit Opportunity Act (ECOA) and Fair Housing Act prohibit discrimination based on race, color, religion, national origin, sex, familial status, or disability. Every borrower must be evaluated on identical criteria.",
        },
        {
          title: "Privacy & Data Handling",
          body: "Borrower data is confidential. Never share loan details, income, or credit information outside of the deal team. Do not discuss deals in public spaces or over unsecured channels.",
        },
        {
          title: "Do's and Don'ts",
          body: "DO: document everything in the CRM. DO: escalate compliance questions to your manager immediately. DON'T: make promises about rates or approvals before underwriting. DON'T: accept cash, gifts, or off-platform payments from borrowers.",
        },
      ],
    },
  ],
};

// ─── trainingData.js ──────────────────────────────────────────────────────────
// Training content organized into three sections.
// All modules are unlocked and retakeable.
// Actual lesson content lives in lessonData.jsx, keyed by module id.
// ─────────────────────────────────────────────────────────────────────────────

export const TRAINING_SECTIONS = [
  {
    id: "products",
    label: "Loan Products",
    desc: "Master every product in our lending lineup.",
    modules: [
      {
        id: "tm_bridge",
        title: "Bridge / Fix & Flip",
        subtitle: "Short-term asset-based financing for flips and renovations",
        duration: "25 min",
        color: "#0ea5e9",
        lessonCount: 5,
      },
      {
        id: "tm_groundup",
        title: "Ground Up Construction",
        subtitle: "Financing new builds from land to certificate of occupancy",
        duration: "30 min",
        color: "#7c3aed",
        lessonCount: 5,
      },
      {
        id: "tm_multifamily",
        title: "Multifamily 1–4 Units",
        subtitle: "Purchase and refinance for small residential rental properties",
        duration: "25 min",
        color: "#2563eb",
        lessonCount: 5,
      },
      {
        id: "tm_dscr",
        title: "DSCR Term Loans",
        subtitle: "Long-term rental financing without income docs",
        duration: "30 min",
        color: "#16a34a",
        lessonCount: 6,
      },
    ],
  },
  {
    id: "sales",
    label: "Sales & Closing",
    desc: "Scripts, objection handling, and pipeline mastery.",
    modules: [
      {
        id: "tm_discovery",
        title: "The Discovery Call",
        subtitle: "Qualify, pitch, and close in 20 minutes",
        duration: "20 min",
        color: "#ea580c",
        lessonCount: 5,
      },
      {
        id: "tm_prospecting",
        title: "Prospecting & Lead Gen",
        subtitle: "Building a consistent pipeline from scratch",
        duration: "Coming soon",
        color: "#94a3b8",
        lessonCount: 0,
        placeholder: true,
      },
      {
        id: "tm_closing",
        title: "Advanced Closing",
        subtitle: "Term sheets, negotiations, and getting to funded",
        duration: "Coming soon",
        color: "#94a3b8",
        lessonCount: 0,
        placeholder: true,
      },
    ],
  },
  {
    id: "company",
    label: "Company & Tools",
    desc: "Systems, compliance, and culture.",
    modules: [
      {
        id: "tm_company",
        title: "Propertunity 101",
        subtitle: "Company overview, culture, and your role",
        duration: "15 min",
        color: "#2563eb",
        lessonCount: 4,
      },
      {
        id: "tm_crm",
        title: "CRM Mastery",
        subtitle: "Work your pipeline like a pro",
        duration: "20 min",
        color: "#ca8a04",
        lessonCount: 5,
      },
      {
        id: "tm_compliance",
        title: "Compliance & Fair Lending",
        subtitle: "Required — legal and regulatory basics",
        duration: "35 min",
        color: "#dc2626",
        lessonCount: 5,
        required: true,
      },
    ],
  },
];

// Flat list of all non-placeholder module ids, used for progress counting
export const ALL_MODULE_IDS = TRAINING_SECTIONS
  .flatMap(s => s.modules)
  .filter(m => !m.placeholder)
  .map(m => m.id);